import { jsx as _jsx } from "react/jsx-runtime";
// src/plugins/builder/templates/social-cross-post/generator.tsx
// Webhook-out template that posts excerpted 5★ reviews to social via Buffer/Zapier.
// "requireConsent" is a small safety guard: if the review text contains the
// reviewer's full name, we skip — encourages the user to ask consent first.
import meta from './template.json';
import { manifestFor, header, readme, assemble, safeString } from '../_helpers';
import { SimplePage } from '../../runtime/SimplePage';
function looksLikeFullName(name) {
    if (!name)
        return false;
    return name.trim().split(/\s+/).length >= 2;
}
const RuntimeUi = (ctx) => {
    const cfg = ctx.config;
    return (_jsx(SimplePage, { title: ctx.instanceName, subtitle: `5★ reviews go to your webhook${cfg.hashtag ? ' with ' + cfg.hashtag : ''}.`, source: "reviews", filterReview: (r) => (r.rating ?? 0) === 5 && (!cfg.requireConsent || !looksLikeFullName(r.author_name)), emptyTitle: "No 5\u2605 reviews ready to share", emptyBody: "When a 5-star review arrives, it'll be sent to your social webhook automatically." }));
};
const runtimeHooks = {
    async onReviewCreated(ctx, review) {
        const r = review;
        const cfg = ctx.config;
        if ((r.rating ?? 0) !== 5)
            return;
        if (cfg.requireConsent && looksLikeFullName(r.author_name))
            return;
        if (!cfg.webhookUrl)
            return;
        if (!ctx.permissions.includes('external:fetch'))
            return;
        const text = `"${(r.body ?? '').slice(0, 200)}" — a happy customer on ${r.platform}. ${cfg.hashtag ?? ''}`.trim();
        await fetch(cfg.webhookUrl, {
            method: 'POST', headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ text, source: 'plugin:builder:social-cross-post' }),
        }).catch(() => { });
    },
};
const generate = (params) => {
    const cfg = params.config;
    const manifest = manifestFor({
        instanceId: params.instanceId, name: params.instanceName,
        description: `Posts 5★ reviews to your social-media webhook${cfg.hashtag ? ' with ' + cfg.hashtag : ''}.`,
        category: 'integration', icon: 'Share2',
        permissions: ['read:reviews', 'external:fetch', 'ui:toast'],
        entry: 'both', hooks: { onReviewCreated: true },
        config: meta.configSchema,
        defaultConfig: { webhookUrl: cfg.webhookUrl, hashtag: cfg.hashtag, requireConsent: cfg.requireConsent },
    });
    const pluginTsx = header({ name: params.instanceName, templateId: meta.id, instanceId: params.instanceId }) +
        `import { useReviews } from '../sdk';
function consentOk(name: string | null) { if (!name) return true; return name.trim().split(/\\s+/).length < 2; }
export default function ${cls(params.instanceId)}() {
  const { reviews } = useReviews({ limit: 100 });
  const ready = (reviews ?? []).filter(r => (r.rating ?? 0) === 5 && (${Boolean(cfg.requireConsent)} ? consentOk(r.author_name) : true));
  return (
    <main id="main" className="mx-auto max-w-3xl px-6 py-10">
      <h1 className="text-2xl font-semibold">${esc(params.instanceName)}</h1>
      <ul className="mt-6 space-y-3">
        {ready.map(r => (<li key={r.id} className="rounded-2xl bg-white p-4 ring-1 ring-slate-200"><p className="text-sm">"{(r.body ?? '').slice(0, 200)}" ${esc(cfg.hashtag)}</p></li>))}
      </ul>
    </main>
  );
}
`;
    const hooksTs = header({ name: params.instanceName, templateId: meta.id, instanceId: params.instanceId }) +
        `import type { PluginHandlers, HandlerContext } from '../types';
function consentOk(name: string | null) { if (!name) return true; return name.trim().split(/\\s+/).length < 2; }
export const handlers: PluginHandlers = {
  async onReviewCreated(ctx: HandlerContext, review: unknown) {
    const r = review as { rating: number | null; body: string | null; author_name: string | null; platform: string };
    if ((r.rating ?? 0) !== 5) return;
    if (${Boolean(cfg.requireConsent)} && !consentOk(r.author_name)) return;
    if (!ctx.permissions.includes('external:fetch')) return;
    const text = \`"\${(r.body ?? '').slice(0, 200)}" — a happy customer on \${r.platform}. ${esc(cfg.hashtag)}\`;
    await fetch('${esc(cfg.webhookUrl)}', { method: 'POST', headers: {'content-type':'application/json'}, body: JSON.stringify({ text }) }).catch(() => {});
  },
};
export default handlers;
`;
    return assemble({ manifest, pluginTsx, hooksTs,
        readmeMd: readme({ name: params.instanceName, templateId: meta.id,
            description: `Sends 5★ reviews to your social-post webhook.`,
            configSummary: [['webhookUrl', cfg.webhookUrl], ['hashtag', cfg.hashtag], ['requireConsent', String(cfg.requireConsent)]],
        }),
    });
};
function esc(s) { return safeString(String(s)); }
function cls(id) { return 'P_' + id.replace(/[^a-zA-Z0-9]+/g, '_'); }
const template = {
    id: meta.id, name: meta.name, tagline: meta.tagline, icon: meta.icon,
    category: meta.category,
    permissions: meta.permissions,
    permissionSummary: meta.permissionSummary,
    entry: meta.entry,
    hooks: meta.hooks,
    configSchema: meta.configSchema,
    defaultConfig: meta.defaultConfig,
    suggestName: () => 'Social cross-poster',
    describe: (cfg) => `Send every 5★ review to your social webhook${cfg.hashtag ? ' with ' + cfg.hashtag : ''}${cfg.requireConsent ? ', skipping anyone who left a full name' : ''}.`,
    generate, runtimeUi: RuntimeUi, runtimeHooks,
    clickCount: meta.clickCount,
};
export default template;
